import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { ZuiShellBase } from './zui-shell-base';
import { style } from './zui-shell-banner-css.js';
import { bannerHeightChangeEventName, pageScrollChangeEventName } from './utils/shell-state';
import type { PropertyValues } from 'lit';
import type { ZuiShell } from './zui-shell';

/**
 * `<zui-shell-banner>` is used to display system information to the user.
 *
 * @element zui-shell-banner
 *
 * @slot - Default, unnamed slot; for inserting content into `<zui-shell-banner>`
 *
 */
export class ZuiShellBanner extends ZuiShellBase {
  /**
   * The type determines which `zui-icon` to display and which color theme to apply
   * @type {"info" | "success" | "error" | "warning"}
   */
  @property({ type: String, attribute: 'type', reflect: true }) type: ZuiShellBannerType = 'info';

  /**
   * Determines whether the banner can be dismissed
   */
  @property({ type: Boolean, attribute: 'dismissible', reflect: true }) dismissible: boolean = false;

  /**
   * Query banner wrapper
   */
  @query('#banner-wrapper') _bannerWrapper: HTMLElement;

  #isDismissed = false;
  #height = 0;

  static get styles() {
    return [super.styles, style];
  }

  constructor() {
    super();
    this._observeBannerHeightChange = true;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(pageScrollChangeEventName, (e) => this.#onTopbarVisibility(e));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(pageScrollChangeEventName, (e) => this.#onTopbarVisibility(e));
  }

  #computeIcon(type: string) {
    switch (type) {
      case 'error':
        return 'zui-error';
      case 'success':
        return 'zui-success';
      case 'warning':
        return 'zui-warning';
      case 'info':
      default:
        return 'zui-info';
    }
  }

  dismiss(): void {
    if (this.dismissible) {
      this.#isDismissed = true;
      this.#dispatchBannerHeightChangeEvent();
    }
  }

  firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);
    new ResizeObserver(() => {
      const style = window.getComputedStyle(this._bannerWrapper);
      const currentHeight = parseFloat(style.getPropertyValue('height'));
      if (currentHeight !== this.#height) {
        this.#height = currentHeight;
        this.#dispatchBannerHeightChangeEvent();
      }
    }).observe(this);
  }

  #onTopbarVisibility(e: Event) {
    const event = e as CustomEvent;
    const topbarVisible = event.detail.topbarVisible;

    if (topbarVisible) {
      this.#showBanner();
    } else {
      this.#hideBanner();
    }
  }

  #showBanner() {
    this._state.bannerScroll = false;
    this.#dispatchBannerHeightChangeEvent();
  }

  #hideBanner() {
    this._state.bannerScroll = true;
    this.#dispatchBannerHeightChangeEvent();
  }

  #dispatchBannerHeightChangeEvent() {
    const height = this._state.bannerScroll || this.#isDismissed ? 0 : this.#height;
    const cssHeight = `${height}px`;

    this._state.bannerHeight = cssHeight;

    const zuiShell: ZuiShell | null = document.querySelector('zui-shell');
    if (zuiShell) {
      zuiShell?.style.setProperty('--zui-shell-banner-height', cssHeight);
    } else {
      const parent: HTMLElement | null = this.parentElement;
      parent?.style.setProperty('--zui-shell-banner-height', cssHeight);
    }

    this.dispatchEvent(
      new CustomEvent(bannerHeightChangeEventName, { detail: { height: cssHeight }, bubbles: true, composed: true })
    );
  }

  render() {
    const icon = this.#computeIcon(this.type);
    const bannerStyle: { [cssProp: string]: string } = {};
    if (this.#isDismissed || this._state.bannerScroll) {
      bannerStyle.transform = `translateY(calc(${this.#height}px * -1))`;
    }

    return html`
      <div style="${styleMap(bannerStyle)}" id="banner-wrapper" class="banner-wrapper">
        <div class="banner-message">
          <zui-icon class="small banner-icon" icon="${icon}"></zui-icon>
          <slot></slot
        ></div>
        <zui-button
          class="banner-close-button link icon-only 
            ${classMap({
            hide: this.dismissible === false,
          })}"
          @click="${this.dismiss}"
          tabindex="0"
          aria-label="close"
        >
          <zui-icon icon="zui-remove" class="small"></zui-icon>
        </zui-button>
      </div>
    `;
  }
}

window.customElements.define('zui-shell-banner', ZuiShellBanner);

export type ZuiShellBannerType = 'info' | 'success' | 'error' | 'warning';

declare global {
  interface HTMLElementTagNameMap {
    'zui-shell-banner': ZuiShellBanner;
  }
}
